import type { AccessibleTenant, SessionSnapshot } from '../shared/api/auth';
import {
  createDataKey,
  createDeviceKey,
  createSalt,
  deriveWrappingKey,
  offlineCryptoAvailable,
  openJson,
  PBKDF2_ITERATIONS,
  sealJson,
  unwrapDataKey,
  wrapDataKey,
} from './crypto';
import { offlineStore } from './store';

/**
 * The device vault: what an operator has to get past to open Varza with no connection.
 *
 * It holds the wrapped data key, the session it was enrolled from, and the counters that
 * throttle guessing. Everything sensitive inside it is sealed; the plain fields are the ones
 * the unlock screen needs before anything has been unlocked — whose account this copy belongs
 * to, when the server last confirmed it, and whether guessing is currently barred.
 */

const VAULT_KEY = 'device';
const DEVICE_KEY = 'device-key';

/**
 * How long an offline copy stays usable without the server confirming the account again.
 *
 * A revoked user, a suspended tenant or a changed password can only be learned about online,
 * so the copy expires on its own and a stale device is pushed back to a real sign-in.
 */
export const MAX_OFFLINE_DAYS = 7;

/** Guessing is barred from the third consecutive failure, and the bar grows quickly. */
const LOCKOUT_AFTER_ATTEMPTS = 3;
const LOCKOUT_BASE_SECONDS = 5;
const LOCKOUT_MAX_SECONDS = 15 * 60;

interface VaultRecord {
  readonly version: 1;
  readonly userId: string;
  /** The name shown on the unlock screen. Not secret: it is what the operator typed to sign in. */
  readonly userLabel: string;
  readonly salt: string;
  readonly iterations: number;
  readonly passwordWrappedKey: string;
  /** Present only while a signed-in session exists on this device; dropped on sign-out. */
  readonly deviceWrappedKey: string | null;
  readonly createdAt: string;
  readonly lastOnlineAt: string;
  readonly failedAttempts: number;
  readonly lockedUntil: string | null;
  readonly sealedSession: string;
  readonly sealedTenants: string;
}

export interface VaultSummary {
  readonly userId: string;
  readonly userLabel: string;
  readonly enrolledAt: string;
  readonly lastOnlineAt: string;
  readonly expiresAt: string;
  readonly expired: boolean;
  readonly failedAttempts: number;
  /** Milliseconds until another attempt is allowed; `0` when one is allowed now. */
  readonly lockedForMs: number;
  /** True while a signed-in session on this device can refresh the copy without the password. */
  readonly deviceUnlockAvailable: boolean;
}

export interface VaultContents {
  readonly session: SessionSnapshot;
  readonly tenants: readonly AccessibleTenant[];
}

export class VaultLockedError extends Error {
  readonly retryAfterMs: number;

  constructor(retryAfterMs: number) {
    super('Too many failed attempts to open the offline copy.');
    this.name = 'VaultLockedError';
    this.retryAfterMs = retryAfterMs;
  }
}

export class VaultPasswordError extends Error {
  readonly failedAttempts: number;

  constructor(failedAttempts: number) {
    super('That password does not open the offline copy on this device.');
    this.name = 'VaultPasswordError';
    this.failedAttempts = failedAttempts;
  }
}

export class VaultExpiredError extends Error {
  constructor() {
    super('The offline copy has expired and needs a connected sign-in.');
    this.name = 'VaultExpiredError';
  }
}

/**
 * The unwrapped data key, for as long as this document lives.
 *
 * It is deliberately not persisted anywhere. A reload with no connection therefore asks for the
 * password again, which is the point.
 */
let dataKey: CryptoKey | null = null;

function readRecord(): Promise<VaultRecord | undefined> {
  return offlineStore().get<VaultRecord>('vault', VAULT_KEY);
}

function writeRecord(record: VaultRecord): Promise<void> {
  return offlineStore().put('vault', VAULT_KEY, record);
}

function expiryOf(record: VaultRecord): number {
  return Date.parse(record.lastOnlineAt) + MAX_OFFLINE_DAYS * 24 * 60 * 60 * 1000;
}

function lockoutRemaining(record: VaultRecord, now = Date.now()): number {
  if (record.lockedUntil === null) return 0;
  return Math.max(0, Date.parse(record.lockedUntil) - now);
}

function nextLockout(failedAttempts: number, now: number): string | null {
  if (failedAttempts < LOCKOUT_AFTER_ATTEMPTS) return null;
  const seconds = Math.min(LOCKOUT_MAX_SECONDS, LOCKOUT_BASE_SECONDS * 2 ** (failedAttempts - LOCKOUT_AFTER_ATTEMPTS));
  return new Date(now + seconds * 1000).toISOString();
}

function summarize(record: VaultRecord): VaultSummary {
  const expiresAt = expiryOf(record);
  return {
    userId: record.userId,
    userLabel: record.userLabel,
    enrolledAt: record.createdAt,
    lastOnlineAt: record.lastOnlineAt,
    expiresAt: new Date(expiresAt).toISOString(),
    expired: Date.now() > expiresAt,
    failedAttempts: record.failedAttempts,
    lockedForMs: lockoutRemaining(record),
    deviceUnlockAvailable: record.deviceWrappedKey !== null,
  };
}

/** What the unlock screen and the device settings panel can know before anything is unlocked. */
export async function readVaultSummary(): Promise<VaultSummary | null> {
  if (!offlineCryptoAvailable()) return null;
  try {
    const record = await readRecord();
    return record === undefined ? null : summarize(record);
  } catch {
    // A browser that refuses IndexedDB — private windows in some configurations — simply has
    // no offline copy, which is a state the application already handles.
    return null;
  }
}

/** True once a data key is held in memory, so caching and queueing can encrypt. */
export function vaultUnlocked(): boolean {
  return dataKey !== null;
}

/** The data key, for the cache and the outbox. Null means: store nothing. */
export function currentDataKey(): CryptoKey | null {
  return dataKey;
}

async function deviceKey(create: boolean): Promise<CryptoKey | null> {
  const existing = await offlineStore().get<CryptoKey>('meta', DEVICE_KEY);
  if (existing !== undefined) return existing;
  if (!create) return null;
  const generated = await createDeviceKey();
  await offlineStore().put('meta', DEVICE_KEY, generated);
  return generated;
}

export interface EnrollmentInput {
  readonly password: string;
  readonly userLabel: string;
  readonly session: SessionSnapshot;
  readonly tenants: readonly AccessibleTenant[];
}

/**
 * Enrols this device, or re-seals an existing enrolment from a fresh online sign-in.
 *
 * Re-enrolling generates a new data key, which discards nothing: the cache is rewritten as
 * pages are read again, and a queue of unsent writes is drained before the caller gets here
 * (see `OfflineProvider`, which refuses to re-enrol while anything is pending).
 */
export async function enrollVault(input: EnrollmentInput): Promise<void> {
  const now = new Date().toISOString();
  const salt = createSalt();
  const wrappingKey = await deriveWrappingKey(input.password, salt, PBKDF2_ITERATIONS);
  const key = await createDataKey();
  const device = await deviceKey(true);

  await writeRecord({
    version: 1,
    userId: input.session.userId,
    userLabel: input.userLabel,
    salt,
    iterations: PBKDF2_ITERATIONS,
    passwordWrappedKey: await wrapDataKey(wrappingKey, key),
    deviceWrappedKey: device === null ? null : await wrapDataKey(device, key),
    createdAt: now,
    lastOnlineAt: now,
    failedAttempts: 0,
    lockedUntil: null,
    sealedSession: await sealJson(key, input.session),
    sealedTenants: await sealJson(key, input.tenants),
  });

  dataKey = key;
}

/**
 * Records that the server has just confirmed this account, and re-seals the session snapshot
 * the offline copy will open with. Called after every successful online sign-in and session
 * probe, which is what keeps the seven-day window rolling forward.
 */
export async function refreshVault(session: SessionSnapshot, tenants: readonly AccessibleTenant[], userLabel: string): Promise<void> {
  const record = await readRecord();
  if (record === undefined || dataKey === null) return;
  if (record.userId !== session.userId) return;

  await writeRecord({
    ...record,
    userLabel: userLabel || record.userLabel,
    lastOnlineAt: new Date().toISOString(),
    failedAttempts: 0,
    lockedUntil: null,
    sealedSession: await sealJson(dataKey, session),
    sealedTenants: await sealJson(dataKey, tenants),
  });
}

/** Opens the vault with the operator's password. This is the offline sign-in. */
export async function unlockVault(password: string): Promise<VaultContents> {
  const record = await readRecord();
  if (record === undefined) throw new VaultExpiredError();

  const waiting = lockoutRemaining(record);
  if (waiting > 0) throw new VaultLockedError(waiting);
  if (Date.now() > expiryOf(record)) throw new VaultExpiredError();

  const wrappingKey = await deriveWrappingKey(password, record.salt, record.iterations);
  let key: CryptoKey;
  try {
    key = await unwrapDataKey(wrappingKey, record.passwordWrappedKey);
  } catch {
    const now = Date.now();
    const failedAttempts = record.failedAttempts + 1;
    await writeRecord({ ...record, failedAttempts, lockedUntil: nextLockout(failedAttempts, now) });
    throw new VaultPasswordError(failedAttempts);
  }

  const contents: VaultContents = {
    session: await openJson<SessionSnapshot>(key, record.sealedSession),
    tenants: await openJson<readonly AccessibleTenant[]>(key, record.sealedTenants),
  };

  dataKey = key;
  if (record.failedAttempts !== 0 || record.lockedUntil !== null) {
    await writeRecord({ ...record, failedAttempts: 0, lockedUntil: null });
  }
  return contents;
}

/**
 * Opens the vault without the password, using the non-extractable device key.
 *
 * Only ever called once the server has confirmed the session on this very load, so it never
 * substitutes for the offline sign-in — it only spares an already-authenticated operator from
 * retyping their password after a reload.
 */
export async function unlockVaultWithDevice(): Promise<boolean> {
  const record = await readRecord();
  if (record?.deviceWrappedKey == null) return false;
  const device = await deviceKey(false);
  if (device === null) return false;

  try {
    dataKey = await unwrapDataKey(device, record.deviceWrappedKey);
    return true;
  } catch {
    return false;
  }
}

/** Forgets the data key held in memory, leaving the sealed copy on the device. */
export function lockVault(): void {
  dataKey = null;
}

/**
 * Signing out: the copy stays, but only the password can open it again. The device wrapping is
 * removed along with the key it used, so an unattended signed-out browser is no longer a way in.
 */
export async function surrenderDeviceUnlock(): Promise<void> {
  dataKey = null;
  const record = await readRecord();
  if (record !== undefined && record.deviceWrappedKey !== null) {
    await writeRecord({ ...record, deviceWrappedKey: null });
  }
  await offlineStore().remove('meta', DEVICE_KEY);
}

/** Removes the vault, the cached reads and the queue. Unsent writes are lost, so ask first. */
export async function wipeVault(): Promise<void> {
  dataKey = null;
  await offlineStore().clearAll();
}
